import React, { useMemo, useRef, useState } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { useAuth } from '../../contexts/AuthContext';
import { useProfile } from '../../contexts/ProfileContext';
import { InvestorProfile as InvestorProfileType } from '../../types/firestore';
import profileGrey from '../../assets/profile_icon__grey-correct_.png';

const PROFILE_PHOTO_MAX_BYTES = 1 * 1024 * 1024;
const PROFILE_PHOTO_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
import {
  INDUSTRY_DISPLAY_MAP,
  BUSINESS_MODEL_DISPLAY_MAP,
  PRODUCT_FOCUS_DISPLAY_MAP,
  STAGE_DISPLAY_MAP,
  CURRENCY_DISPLAY_MAP,
  INVESTMENT_TYPE_DISPLAY_MAP,
  EXPERTISE_DISPLAY_MAP,
  getDisplayName,
} from '../../constants/display-mappings';
import { formatCompactAmount } from '../../utils/formatCompactAmount';
import { isUnitedStatesCountry } from '../../utils/investorPricingView';
import { SUCCESS_FEE_LIVE } from '../../constants/successFee';
import { ownershipSelectOptions, tractionSelectOptions } from '../../constants/ownership';
// Import icons (matching old prairi-web design)
import companyIcon from '../../assets/icons/company2.png';
import previousInvestmentsIcon from '../../assets/stage4.png';
import companyNameIcon from '../../assets/icons/company.png';
import locationIcon from '../../assets/icons/location.png';
import industryIcon from '../../assets/icons/industry.png';
import productIcon from '../../assets/icons/product.png';
import bizModelIcon from '../../assets/icons/biz_model.gif';
import stageIcon from '../../assets/icons/stage.gif';
import amountIcon from '../../assets/icons/amount.gif';
import foundersIcon from '../../assets/icons/founders.png';
import ownershipIcon from '../../assets/icons/ownership.png';
import tractionIcon from '../../assets/icons/traction.png';
import expertiseIcon from '../../assets/icons/expertise.png';
import supportIcon from '../../assets/icons/support.png';
import sdgIcon from '../../assets/icons/sdglogo.png';

// Import SDG images
import sdg01 from '../../assets/SDG/E-WEB-Goal-01.png';
import sdg02 from '../../assets/SDG/E-WEB-Goal-02.png';
import sdg03 from '../../assets/SDG/E-WEB-Goal-03.png';
import sdg04 from '../../assets/SDG/E-WEB-Goal-04.png';
import sdg05 from '../../assets/SDG/E-WEB-Goal-05.png';
import sdg06 from '../../assets/SDG/E-WEB-Goal-06.png';
import sdg07 from '../../assets/SDG/E-WEB-Goal-07.png';
import sdg08 from '../../assets/SDG/E-WEB-Goal-08.png';
import sdg09 from '../../assets/SDG/E-WEB-Goal-09.png';
import sdg10 from '../../assets/SDG/E-WEB-Goal-10.png';
import sdg11 from '../../assets/SDG/E-WEB-Goal-11.png';
import sdg12 from '../../assets/SDG/E-WEB-Goal-12.png';
import sdg13 from '../../assets/SDG/E-WEB-Goal-13.png';
import sdg14 from '../../assets/SDG/E-WEB-Goal-14.png';
import sdg15 from '../../assets/SDG/E-WEB-Goal-15.png';
import sdg16 from '../../assets/SDG/E-WEB-Goal-16.png';
import sdg17 from '../../assets/SDG/E-WEB-Goal-17.png';

// SDG images mapping — only rendered for sdgs present in selectedSDGs
const sdgImages: { [key: number]: string } = {
  1: sdg01,
  2: sdg02,
  3: sdg03,
  4: sdg04,
  5: sdg05,
  6: sdg06,
  7: sdg07,
  8: sdg08,
  9: sdg09,
  10: sdg10,
  11: sdg11,
  12: sdg12,
  13: sdg13,
  14: sdg14,
  15: sdg15,
  16: sdg16,
  17: sdg17,
};

// Full descriptive labels for investment types — mirrors the investor Company step.
const INVESTMENT_TYPE_INTERESTED_LABELS: { [key: string]: string } = {
  equity: 'Equity investments (cash for ownership)',
  'revenue-based': "Revenue-based financing (lending based on startup's revenue)",
  'services-equity': 'Services in exchange for equity (little or no cash provided)',
};

const founderRepresentationLabels: { [key: string]: string } = {
  woman: 'Woman',
  poc: 'POC',
  lgbtq: 'LGBTQ+',
  immigrant: 'Immigrant',
  personWithDisabilities: 'Person with disabilities',
  seniorEntrepreneur: 'Senior/65+',
  teenEntrepreneur: 'Teen/19-',
};

interface InfoRowProps {
  icon: string | React.ReactNode;
  value: string | React.ReactNode;
  label?: string;
  labelClassName?: string;
  valueClassName?: string;
}

const InfoRow: React.FC<InfoRowProps> = ({
  icon,
  value,
  label,
  labelClassName,
  valueClassName,
}) => {
  if (!value) return null;

  return (
    <div className="flex items-start space-x-3 sm:space-x-4 py-2 sm:py-2.5">
      <div className="w-7 h-7 sm:w-8 sm:h-8 flex-shrink-0 flex items-center justify-center">
        {typeof icon === 'string' ? (
          <img src={icon} alt="" className="w-full h-full object-contain" loading="lazy" />
        ) : (
          icon
        )}
      </div>
      <div className="flex-1 text-left min-w-0">
        {label && (
          <div className={labelClassName || 'text-white text-base font-medium mb-1'}>{label}</div>
        )}
        <div className={valueClassName || 'text-white text-base'}>{value}</div>
      </div>
    </div>
  );
};

// Component for expandable lists (shows 8 items, then "View more")
interface ExpandableListProps {
  items: string[];
  maxVisible?: number;
}

const ExpandableList: React.FC<ExpandableListProps> = ({ items, maxVisible = 8 }) => {
  const [isExpanded, setIsExpanded] = React.useState(false);

  if (items.length === 0) return null;

  const visibleItems = isExpanded ? items : items.slice(0, maxVisible);
  const hasMore = items.length > maxVisible;

  return (
    <div>
      <span>{visibleItems.join(', ')}</span>
      {hasMore && (
        <>
          {' '}
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="text-gray-400 hover:text-gray-300 underline italic"
          >
            {isExpanded ? 'View less' : 'View more'}
          </button>
        </>
      )}
    </div>
  );
};

interface InvestorProfileProps {
  profile?: InvestorProfileType | null;
  /** Compact view for startup dashboard cards: name, company, offers, investments, fee status. */
  startupDashboard?: boolean;
  /** Investor's personal name (from users collection). Used in startupDashboard mode. */
  investorName?: string;
  /** Show CTA to add profile picture when none is set. */
  isOwnProfile?: boolean;
  /** Admin deals popup: removes min-h-screen, constrains height for modal display. */
  adminCompact?: boolean;
  /** Startup-facing view: shows only public fields (name, location, offers, previous investments, investment types). Preferences are hidden. */
  startupView?: boolean;
  /** Inbox: green while this investor is in PRAIRI right now, white when they are not. */
  isOnline?: boolean;
  /** Inbox: conversation has ended, so the presence dot stays red. */
  presenceEnded?: boolean;
  /** Action when clicking "Contact this investor" link */
  onContactClick?: () => void;
  /** Inbox chat header photo. Dashboard cards keep the smaller default. */
  chatAvatarClass?: string;
  /** Extra space above the name and company in the inbox chat card. */
  inboxChat?: boolean;
}

const InvestorProfile: React.FC<InvestorProfileProps> = ({
  profile: externalProfile,
  startupDashboard = false,
  investorName,
  isOwnProfile = false,
  adminCompact = false,
  startupView = false,
  isOnline,
  presenceEnded = false,
  onContactClick,
  chatAvatarClass = 'w-12 h-12',
  inboxChat = false,
}) => {
  const { user } = useAuth();
  const { investorProfile: contextProfile, loading, reloadProfile } = useProfile();
  const investorProfile = externalProfile !== undefined ? externalProfile : contextProfile;

  const rawProfilePhotoEarly = (investorProfile as { profilePictureUrl?: string } | null)
    ?.profilePictureUrl;
  const profilePhotoUrlEarly =
    typeof rawProfilePhotoEarly === 'string' ? rawProfilePhotoEarly.trim() : '';
  const [localPhotoUrl, setLocalPhotoUrl] = useState<string | null>(null);
  const [profilePhotoFailed, setProfilePhotoFailed] = useState(false);
  const [photoUploading, setPhotoUploading] = useState(false);
  const [photoError, setPhotoError] = useState<string | null>(null);
  const photoInputRef = useRef<HTMLInputElement>(null);
  React.useEffect(() => {
    setProfilePhotoFailed(false);
  }, [profilePhotoUrlEarly, localPhotoUrl]);

  const handleUploadPhotoClick = () => {
    if (photoUploading) return;
    setPhotoError(null);
    photoInputRef.current?.click();
  };

  const handlePhotoSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !user?.id) return;

    setPhotoError(null);
    if (!PROFILE_PHOTO_TYPES.includes(file.type)) {
      setPhotoError('Please upload a JPEG, PNG, or WebP image.');
      return;
    }
    if (file.size > PROFILE_PHOTO_MAX_BYTES) {
      setPhotoError('Image must be smaller than 1 MB.');
      return;
    }

    setPhotoUploading(true);
    try {
      const reader = new FileReader();
      const base64 = await new Promise<string>((resolve, reject) => {
        reader.onload = () => {
          const result = reader.result as string;
          resolve(result.split(',')[1]);
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });

      const upload = httpsCallable<
        { userId: string; file: { name: string; contentType: string; data: string } },
        { success: boolean; url?: string; error?: string }
      >(getFunctions(), 'uploadInvestorPicture');

      const result = await upload({
        userId: user.id,
        file: { name: file.name, contentType: file.type, data: base64 },
      });

      if (result.data.success && result.data.url) {
        const storedUrl = result.data.url;
        const freshUrl = storedUrl.includes('v=')
          ? storedUrl
          : `${storedUrl}${storedUrl.includes('?') ? '&' : '?'}v=${Date.now()}`;
        setLocalPhotoUrl(freshUrl);
        setProfilePhotoFailed(false);
        await reloadProfile();
      } else {
        setPhotoError(result.data.error || 'Upload failed. Please try again.');
      }
    } catch {
      setPhotoError('Upload failed. Please try again.');
    } finally {
      setPhotoUploading(false);
      if (photoInputRef.current) photoInputRef.current.value = '';
    }
  };

  const heightClass = adminCompact ? '' : 'min-h-screen';

  // Memoized derived values — only recomputed when investorProfile reference changes
  const investmentRange = useMemo(() => {
    if (!investorProfile) return null;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const range = (investorProfile as any)?.investmentRange;
    if (!range || !Array.isArray(range) || range.length !== 2) return null;
    const currency = getDisplayName(CURRENCY_DISPLAY_MAP, investorProfile.currency || 'usd');
    const [min, max] = range;
    return `${formatCompactAmount(min)} - ${formatCompactAmount(max)} ${currency}`;
  }, [investorProfile]);

  const investAs = useMemo(() => {
    if (!investorProfile?.investAs || investorProfile.investAs.length === 0) return null;
    return investorProfile.investAs
      .map(type =>
        getDisplayName(
          INVESTMENT_TYPE_DISPLAY_MAP,
          type as keyof typeof INVESTMENT_TYPE_DISPLAY_MAP
        )
      )
      .join(', ');
  }, [investorProfile]);

  // Startup-facing "Investor is interested in:" — full descriptive investment-type labels.
  const investAsInterested = useMemo((): string[] => {
    const types = investorProfile?.investAs || [];
    if (types.length === 0) return [];
    return types.map(t => INVESTMENT_TYPE_INTERESTED_LABELS[t as string]).filter(Boolean);
  }, [investorProfile]);

  const countriesArray = useMemo((): string[] => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const countries = (investorProfile as any)?.countries || [];
    if (countries.length === 0) return [];
    const concrete = countries.filter((c: string) => c !== 'ANYWHERE');
    const shown =
      concrete.length > 0 ? concrete : countries.includes('ANYWHERE') ? ['Worldwide'] : [];
    if (shown.length === 0) return [];
    return shown.map((c: string) =>
      c
        .split(' ')
        .map((w: string) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
        .join(' ')
    );
  }, [investorProfile]);

  const industriesDisplay = useMemo(() => {
    if (!investorProfile?.industries) return [];
    return investorProfile.industries.map(item =>
      getDisplayName(INDUSTRY_DISPLAY_MAP, item as keyof typeof INDUSTRY_DISPLAY_MAP)
    );
  }, [investorProfile]);

  const businessModelDisplay = useMemo(() => {
    if (!investorProfile?.businessModelPreference) return [];
    return investorProfile.businessModelPreference.map(item =>
      getDisplayName(BUSINESS_MODEL_DISPLAY_MAP, item as keyof typeof BUSINESS_MODEL_DISPLAY_MAP)
    );
  }, [investorProfile]);

  const productFocusDisplay = useMemo(() => {
    if (!investorProfile?.productFocusPreference) return [];
    return investorProfile.productFocusPreference.map(item =>
      getDisplayName(PRODUCT_FOCUS_DISPLAY_MAP, item as keyof typeof PRODUCT_FOCUS_DISPLAY_MAP)
    );
  }, [investorProfile]);

  const stagesDisplay = useMemo(() => {
    if (!investorProfile?.stages) return [];
    return investorProfile.stages.map(item =>
      getDisplayName(STAGE_DISPLAY_MAP, item as keyof typeof STAGE_DISPLAY_MAP)
    );
  }, [investorProfile]);

  const founderRepresentation = useMemo(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const representation = (investorProfile as any)?.founderRepresentation || [];
    if (representation.length === 0) return null;
    return representation
      .map((rep: string) => founderRepresentationLabels[rep])
      .filter(Boolean)
      .join(', ');
  }, [investorProfile]);

  const capitalization = useMemo(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const caps = (investorProfile as any)?.capitalization || [];
    if (caps.length === 0) return null;
    return caps
      .map((cap: string) => {
        const opt = ownershipSelectOptions.find(o => o.value === cap);
        return opt ? opt.name : cap;
      })
      .join(', ');
  }, [investorProfile]);

  const traction = useMemo(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const t = (investorProfile as any)?.traction || [];
    if (t.length === 0) return null;
    return t
      .map((v: string) => {
        const opt = tractionSelectOptions.find(o => o.value === v);
        return opt ? opt.name : v;
      })
      .join(', ');
  }, [investorProfile]);

  const expertise = useMemo(() => {
    if (!investorProfile?.expertise || investorProfile.expertise.length === 0) return null;
    const list = investorProfile.expertise
      .map(exp => getDisplayName(EXPERTISE_DISPLAY_MAP, exp as keyof typeof EXPERTISE_DISPLAY_MAP))
      .join(', ');
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const otherExp = (investorProfile as any)?.otherExpertise;
    if (investorProfile.expertise.includes('other') && otherExp?.trim()) {
      return `${list}: ${otherExp}`;
    }
    return list;
  }, [investorProfile]);

  const preferStartupProgram = useMemo(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const prefer = (investorProfile as any)?.preferStartupProgram;
    if (!prefer) return null;
    if (prefer === 'yes') return 'Yes';
    if (prefer === 'no') return 'No';
    if (prefer === 'doesnt-matter') return "Doesn't matter";
    return prefer;
  }, [investorProfile]);

  if (loading) {
    return (
      <div className={`${heightClass} bg-dark-950 flex items-center justify-center py-16`}>
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-emerald-500 mb-4"></div>
          <p className="text-white text-lg">Loading profile...</p>
        </div>
      </div>
    );
  }

  if (!investorProfile) {
    return (
      <div className={`${heightClass} bg-dark-950 flex items-center justify-center py-16`}>
        <div className="text-center">
          <p className="text-white text-lg">No profile found</p>
        </div>
      </div>
    );
  }

  const profilePhotoUrl = (localPhotoUrl || profilePhotoUrlEarly).trim();
  const showProfilePhoto = Boolean(profilePhotoUrl) && !profilePhotoFailed;
  const placeholderBg = startupView || startupDashboard ? 'bg-[#333333]' : 'bg-dark-900';
  const onProfilePhotoError = () => setProfilePhotoFailed(true);

  // Full profile header: show the person's name as the title and the company as a
  // subtitle. The person's name lives on the users doc (not the investor profile),
  // so use the passed investorName or, for your own profile, the signed-in user's name.
  const investorPersonName = (investorName || (isOwnProfile ? user?.name : '') || '').trim();
  const headerTitle = investorPersonName || investorProfile.companyName || 'Investor Name';
  const headerCompanySubtitle =
    investorPersonName && investorProfile.companyName ? investorProfile.companyName : null;

  const renderProfileAvatar = (sizeClass: string) =>
    showProfilePhoto ? (
      <img
        src={profilePhotoUrl}
        alt="Investor"
        onError={onProfilePhotoError}
        className={`${sizeClass} rounded-full flex-shrink-0 border border-[#444444] object-cover`}
        loading="lazy"
      />
    ) : (
      <span
        className={`${sizeClass} flex flex-shrink-0 items-center justify-center overflow-hidden rounded-full border border-[#444444] ${placeholderBg}`}
      >
        <img
          src={profileGrey}
          alt=""
          aria-hidden
          className="h-[84%] w-[84%] translate-y-[14%] object-contain"
        />
      </span>
    );

  const formatPreferred = (value: string | undefined): string | null => {
    if (!value) return null;
    if (value === 'no-preference') return 'No preference';
    if (value === '1') return '1+';
    if (value === '2') return '2+';
    if (value === '3') return '3+';
    if (value === '1+' || value === '2+' || value === '3+') return value;
    return value;
  };

  if (startupView) {
    return (
      <div className="bg-transparent p-6 h-full min-w-0 w-full">
        <div className="mb-6 flex items-center gap-4 min-w-0">
          <div className="relative flex-shrink-0">
            {renderProfileAvatar('w-16 h-16')}
            {presenceEnded ? (
              <span
                className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full ring-2 ring-[#333333] bg-red-500"
                aria-hidden
              />
            ) : isOnline === true ? (
              <span
                className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full ring-2 ring-[#333333] bg-emerald-400"
                aria-hidden
              />
            ) : null}
          </div>
          <div className="text-left min-w-0 flex-1">
            <h2 className="text-xl sm:text-2xl font-bold text-white leading-snug break-words">
              {investorProfile.companyName || 'Investor'}
            </h2>
            {investorName && investorName !== investorProfile.companyName && (
              <p className="text-xs text-gray-400 mt-0.5 break-words">{investorName}</p>
            )}
          </div>
        </div>

        <div className="space-y-1">
          <InfoRow icon={locationIcon} value={investorProfile.country} />
          <InfoRow icon={expertiseIcon} label="What they offer:" value={expertise} />
          <InfoRow
            icon={previousInvestmentsIcon}
            label="Previous investments:"
            value={(investorProfile as any)?.previousInvestments}
          />
          <InfoRow icon={amountIcon} label="Interested in:" value={investAs} />
        </div>
      </div>
    );
  }

  if (startupDashboard) {
    const personName = (investorName || '').trim() || 'Investor';
    const companyName = (investorProfile.companyName || '').trim();
    const investorCountry = (investorProfile.country || '').trim();
    const showSuccessFee =
      SUCCESS_FEE_LIVE && investorCountry !== '' && !isUnitedStatesCountry(investorCountry);

    return (
      <div className="space-y-3 text-left">
        <div className="flex items-start gap-3 pb-1 pt-1">
          <div className="flex-shrink-0">
            <div className="relative">
              {renderProfileAvatar(chatAvatarClass)}
              {presenceEnded ? (
                <span
                  className="absolute -bottom-0.5 -right-0.5 h-3.5 w-3.5 rounded-full bg-red-500 ring-2 ring-[#333333]"
                  aria-hidden
                />
              ) : isOnline === true ? (
                <span
                  className="absolute -bottom-0.5 -right-0.5 h-3.5 w-3.5 rounded-full bg-emerald-400 ring-2 ring-[#333333]"
                  aria-hidden
                />
              ) : null}
            </div>
            {isOwnProfile && !showProfilePhoto ? (
              <>
                <input
                  ref={photoInputRef}
                  type="file"
                  accept="image/jpeg,image/jpg,image/png,image/webp"
                  className="hidden"
                  onChange={handlePhotoSelected}
                />
                <button
                  type="button"
                  onClick={handleUploadPhotoClick}
                  disabled={photoUploading}
                  className="mt-1.5 block max-w-[5.5rem] text-left text-[11px] leading-tight text-emerald-400 underline underline-offset-2 hover:text-emerald-300 disabled:opacity-50"
                >
                  {photoUploading ? 'Uploading…' : 'Upload photo'}
                </button>
                {photoError ? <p className="mt-1 text-[11px] text-red-400">{photoError}</p> : null}
              </>
            ) : null}
          </div>
          <div className="min-w-0 flex-1 pr-12">
            <p
              className={`text-xl font-bold text-white tracking-tight leading-snug flex items-start gap-1.5 ${
                inboxChat ? 'pt-2' : ''
              }`}
            >
              <span className="min-w-0 break-words">{personName}</span>
              {showSuccessFee && investorProfile.paysFee ? (
                <span className="text-[#92d050] text-base flex-shrink-0">★</span>
              ) : null}
            </p>
          </div>
        </div>

        <div className="space-y-0.5">
          {/* Row 1: Contact — company sits under the investor name above (omitted if empty) */}
          <InfoRow
            icon={companyNameIcon}
            value={
              <>
                {companyName ? (
                  <span
                    className={`mb-1 block break-words text-base font-bold text-gray-300 ${
                      inboxChat ? 'mt-2' : ''
                    }`}
                  >
                    {companyName}
                  </span>
                ) : null}
                {onContactClick ? (
                  <button
                    type="button"
                    onClick={onContactClick}
                    className="text-[#92d050] underline hover:text-[#addc7a] text-xs block text-left transition-colors cursor-pointer"
                  >
                    Contact this investor
                  </button>
                ) : (
                  <span className="text-[#92d050] underline text-xs block">
                    Contact this investor
                  </span>
                )}
              </>
            }
          />

          {/* Row 2: Location */}
          <InfoRow
            icon={locationIcon}
            valueClassName="text-white text-sm font-normal"
            value={investorProfile.country}
          />

          {/* Row 3: Offers */}
          <InfoRow
            icon={expertiseIcon}
            label="Investor offers:"
            labelClassName="text-sm font-bold text-white mb-0.5"
            valueClassName="text-gray-300 text-sm font-normal"
            value={expertise || 'Expertise in the field'}
          />

          {/* Row 4: Previous investments include: */}
          <InfoRow
            icon={previousInvestmentsIcon}
            label="Previous investments include:"
            labelClassName="text-sm font-bold text-white mb-0.5"
            valueClassName="text-gray-300 text-sm font-normal"
            value={(investorProfile as any)?.previousInvestments || 'Lucy, Sally'}
          />

          {/* Row 5: Investment types the investor is interested in (only when set). */}
          {investAsInterested.length > 0 && (
            <InfoRow
              icon={amountIcon}
              label="Investor is interested in:"
              labelClassName="text-sm font-bold text-white mb-0.5"
              value={
                <div className="space-y-0.5">
                  {investAsInterested.map(labelText => {
                    const match = labelText.match(/^(.*?)\s*(\(.*\))$/);
                    const name = match ? match[1] : labelText;
                    const detail = match ? match[2] : '';
                    return (
                      <div key={labelText} className="text-sm leading-snug">
                        <span className="font-bold text-white">{name}</span>
                        {detail ? <span className="text-gray-400"> {detail}</span> : null}
                      </div>
                    );
                  })}
                </div>
              }
            />
          )}
        </div>

        {/* Fee box: US-based investors are outside the 2% success fee. */}
        {showSuccessFee && investorProfile?.paysFee ? (
          <div className="rounded-xl border border-gray-500/70 bg-transparent p-3 text-left">
            <div className="flex items-start gap-2.5">
              <span className="text-[#92d050] text-base leading-none mt-0.5 flex-shrink-0">★</span>
              <div className="min-w-0 flex-1">
                <p className="text-white text-xs sm:text-sm font-bold">Startup-Friendly Investor</p>
                <p className="mt-0.5 text-[11px] sm:text-xs text-gray-300 leading-snug">
                  Has generously agreed to pay PRAIRI&apos;s &apos;success fee&apos; from their own
                  funds
                </p>
              </div>
            </div>
          </div>
        ) : showSuccessFee ? (
          <div className="rounded-xl border border-gray-600/60 bg-transparent p-3 text-left">
            <div className="flex items-start gap-2.5">
              <span className="text-amber-400 text-xs leading-none mt-1 flex-shrink-0">●</span>
              <p className="text-[11px] sm:text-xs text-gray-300 leading-snug">
                PRAIRI&apos;s &apos;success fee&apos; (2% of this investor&apos;s total investment)
                will be allocated from the amount they provide you.
              </p>
            </div>
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <div
      className={`${heightClass} py-4 lg:py-6 ${
        adminCompact
          ? 'max-h-[85vh] overflow-y-auto scrollbar-visible-dark bg-transparent'
          : 'bg-[#262626]'
      }`}
    >
      <div className="container mx-auto px-4 lg:px-6">
        <div className="flex flex-col lg:flex-row gap-4 lg:gap-6 w-full">
          {/* Left column: Profile Information */}
          <div className="flex-1">
            <div
              className={`rounded-lg p-6 lg:p-8 ${adminCompact ? 'bg-transparent' : 'bg-dark-900'}`}
            >
              {/* Header */}
              <div className="mb-8 flex items-center gap-5">
                <div className="relative flex-shrink-0">{renderProfileAvatar('w-20 h-20')}</div>
                <div className="text-left">
                  <h1 className="text-4xl font-bold text-white mb-1">{headerTitle}</h1>
                  {isOwnProfile && !showProfilePhoto && (
                    <div className="text-left">
                      <p className="text-xs text-gray-400">
                        Add a profile photo so startups can put a face to the name.
                      </p>
                      <input
                        ref={photoInputRef}
                        type="file"
                        accept="image/jpeg,image/jpg,image/png,image/webp"
                        className="hidden"
                        onChange={handlePhotoSelected}
                      />
                      <button
                        type="button"
                        onClick={handleUploadPhotoClick}
                        disabled={photoUploading}
                        className="mt-1 inline-block text-sm text-emerald-400 underline underline-offset-2 hover:text-emerald-300 disabled:opacity-50"
                      >
                        {photoUploading ? 'Uploading…' : 'Upload photo'}
                      </button>
                      {photoError && <p className="mt-1 text-xs text-red-400">{photoError}</p>}
                    </div>
                  )}
                </div>
              </div>

              <div className="space-y-1">
                <InfoRow icon={companyNameIcon} value={headerCompanySubtitle} />
                <InfoRow icon={locationIcon} value={investorProfile.country} />
                <InfoRow
                  icon={previousInvestmentsIcon}
                  label="Previous investments:"
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  value={(investorProfile as any)?.previousInvestments}
                />
                <InfoRow
                  icon={companyIcon}
                  label="Investment groups:"
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  value={(investorProfile as any)?.investmentGroups}
                />
                <InfoRow icon={amountIcon} label="Investment types:" value={investAs} />
                <InfoRow
                  icon={locationIcon}
                  label="Acceptable countries/regions:"
                  value={<ExpandableList items={countriesArray} />}
                />
                <InfoRow
                  icon={industryIcon}
                  label="Industries:"
                  value={<ExpandableList items={industriesDisplay} />}
                />
                <InfoRow
                  icon={bizModelIcon}
                  label="Business model preference:"
                  value={businessModelDisplay.join(', ') || null}
                />
                <InfoRow
                  icon={productIcon}
                  label="Product focus preference:"
                  value={productFocusDisplay.join(', ') || null}
                />
                <InfoRow
                  icon={stageIcon}
                  label="Stages:"
                  value={stagesDisplay.join(', ') || null}
                />
                <InfoRow icon={amountIcon} label="Investment range:" value={investmentRange} />
                <InfoRow
                  icon={foundersIcon}
                  label="Preferred number of founders:"
                  value={formatPreferred(
                    (() => {
                      const raw = String(
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        (investorProfile as any)?.preferredFoundersCount || ''
                      );
                      return raw === '2' ? '2+' : raw;
                    })()
                  )}
                />
                <InfoRow
                  icon={foundersIcon}
                  label="Preferred number of non-founder team members:"
                  value={formatPreferred(
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    (investorProfile as any)?.preferredTeamMembersCount
                  )}
                />
                {founderRepresentation && (
                  <InfoRow
                    icon={foundersIcon}
                    label="Founder representation preferences:"
                    value={founderRepresentation}
                  />
                )}
                <InfoRow
                  icon={ownershipIcon}
                  label="Capitalization preferences:"
                  value={capitalization}
                />
                <InfoRow icon={tractionIcon} label="Traction preferences:" value={traction} />
                <InfoRow
                  icon={supportIcon}
                  label="Prefer startup participation in program:"
                  value={preferStartupProgram}
                />
                <InfoRow
                  icon={expertiseIcon}
                  label="Additional value/expertise:"
                  value={expertise}
                />

                {/* SDGs */}
                {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
                {(investorProfile as any)?.selectedSDGs &&
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  (investorProfile as any).selectedSDGs.length > 0 && (
                    <div className="flex flex-col sm:flex-row items-start sm:space-x-4 py-6 mt-4">
                      <div className="w-8 h-8 flex-shrink-0 flex items-center justify-center mb-3 sm:mb-0">
                        <img
                          src={sdgIcon}
                          alt="SDGs"
                          className="w-full h-full object-contain"
                          loading="lazy"
                        />
                      </div>
                      <div className="flex-1 text-left">
                        <div className="text-white text-sm sm:text-base font-medium mb-3 sm:mb-4">
                          Preference toward startups working on the following Sustainable
                          Development Goals:
                        </div>
                        <div className="grid grid-cols-3 sm:grid-cols-6 lg:grid-cols-12 gap-2 sm:gap-2.5">
                          {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
                          {(investorProfile as any).selectedSDGs.map((sdg: number) => (
                            <div key={sdg} className="w-full">
                              <img
                                src={sdgImages[sdg]}
                                alt={`SDG Goal ${sdg}`}
                                loading="lazy"
                                className="w-full h-auto rounded-lg"
                              />
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InvestorProfile;
